import { promotionLineKey, type SavedCartPromotion } from '../cartPromotion';
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth/AuthProvider";
import { ApiError, apiFetch } from "@/lib/api/client";
import { getStripe } from "@/lib/stripe";
import { track } from "@/lib/analytics";
import { cart, type CartItem } from "../stores/cart";
import {
  useSupplementPurchase,
  type SupplementPurchaseRequest,
  type SupplementPurchaseResult,
  type SupplementCatalog,
} from "./api";
import {
  SUPPLEMENT_ANALYTICS_CONTEXT,
  supplementCartProperties,
} from "./analytics";

interface Attempt {
  request: SupplementPurchaseRequest;
  items: CartItem[];
}

const storageKey = (userId: string) =>
  `system.portal.supplement-purchase.${userId}.v1`;

export function readSupplementAttempt(userId: string): Attempt | null {
  try {
    const value = JSON.parse(
      localStorage.getItem(storageKey(userId)) ?? "null",
    ) as Attempt | null;
    return value?.request?.idempotencyKey && Array.isArray(value.items)
      ? value
      : null;
  } catch {
    return null;
  }
}

/** Keep an uncertain payment's identity across drawer closes and page reloads.
 * Only a known terminal result or server-confirmed cancellation releases it. */
export function useSupplementCartPurchase() {
  const { session } = useAuth();
  const userId = session?.user.id;
  const currentUser = useRef(userId);
  currentUser.current = userId;
  const [saved, setSaved] = useState<{
    userId?: string;
    attempt: Attempt | null;
  }>(() => ({
    userId,
    attempt: userId ? readSupplementAttempt(userId) : null,
  }));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [needsPriceReview, setNeedsPriceReview] = useState(false);
  const mutation = useSupplementPurchase();
  const qc = useQueryClient();
  const attempt = saved.userId === userId ? saved.attempt : null;

  useEffect(() => {
    currentUser.current = userId;
    setSaved({
      userId,
      attempt: userId ? readSupplementAttempt(userId) : null,
    });
    setError(null);
    setNeedsPriceReview(false);
    setBusy(false);
    const sync = (event: StorageEvent) => {
      if (userId && event.key === storageKey(userId)) {
        setSaved({ userId, attempt: readSupplementAttempt(userId) });
      }
    };
    window.addEventListener("storage", sync);
    return () => {
      currentUser.current = undefined;
      window.removeEventListener("storage", sync);
    };
  }, [userId]);

  function remember(next: Attempt | null) {
    if (!userId || currentUser.current !== userId)
      throw new Error("Please sign in again before purchasing.");
    if (next) {
      // A purchase must not start unless its retry identity was durably saved.
      localStorage.setItem(storageKey(userId), JSON.stringify(next));
    } else {
      try {
        localStorage.removeItem(storageKey(userId));
      } catch {
        /* A retained terminal attempt is safe to reconcile. */
      }
    }
    setSaved({ userId, attempt: next });
  }

  async function settle(result: SupplementPurchaseResult, original: Attempt) {
    if (currentUser.current !== userId) return false;
    for (const line of result.items) {
      if (
        line.status === "succeeded" ||
        (line.status === "processing" && !line.error)
      ) {
        cart.remove(line.productId);
      }
    }
    const unresolved = result.items.some(
      (line) =>
        line.status === "requires_action" ||
        (line.status === "processing" && Boolean(line.error)),
    );
    if (!unresolved) remember(null);
    else remember(original);
    await Promise.allSettled([
      qc.invalidateQueries({ queryKey: ["supplements", userId, "orders"] }),
      qc.invalidateQueries({ queryKey: ["me", "orders"] }),
    ]);
    if (currentUser.current !== userId) return false;
    const failed = result.items.filter(
      (line) => line.status === "failed" || line.status === "cancelled",
    );
    if (failed.length)
      setError(
        failed
          .map((line) => {
            const title =
              original.items.find((item) => item.id === line.productId)
                ?.title ?? "Supplement";
            return `${title}: ${line.error?.message ?? (line.status === "cancelled" ? "Payment cancelled." : "Payment was not completed. Try another card.")}`;
          })
          .join(" "),
      );
    if (unresolved)
      setError(
        "Your payment still needs verification. Check purchase status to continue, or cancel the unpaid purchase.",
      );
    return !failed.length && !unresolved;
  }

  async function purchase(items: CartItem[], paymentMethodId: string, promotion?: SavedCartPromotion | null) {
    setBusy(true);
    setError(null);
    const persisted = userId ? readSupplementAttempt(userId) : null;
    const original: Attempt = persisted ?? {
      items,
      request: {
        ...(promotion ? { cart_promotion_id: promotion.quote.id } : {}),
        items: items.map((item) => ({
          ...(promotion ? { cart_promotion_line_key: promotionLineKey(item) } : {}),
          productId: item.id,
          quantity: item.quantity,
          expectedUnitPriceCents: item.price_cents,
        })),
        paymentMethodId,
        idempotencyKey: crypto.randomUUID(),
      },
    };
    try {
      if (!persisted) {
        remember(original);
        track("supplement_checkout_started", {
          ...SUPPLEMENT_ANALYTICS_CONTEXT,
          ...supplementCartProperties(
            original.items,
            qc.getQueryData<SupplementCatalog>([
              "supplements",
              userId,
              "catalog",
            ])?.products,
          ),
          cart_attempt_id: original.request.idempotencyKey,
        });
      }
      let result = await mutation.mutateAsync(original.request);
      if (currentUser.current !== userId) return false;
      const action = result.items.filter(
        (line) => line.status === "requires_action",
      );
      if (action.length) {
        if (!result.publishableKey)
          throw new Error(
            "Card verification is unavailable. Check purchase status shortly.",
          );
        const stripe = await getStripe(result.publishableKey);
        if (currentUser.current !== userId) return false;
        if (!stripe)
          throw new Error(
            "Card verification could not load. Check purchase status to retry.",
          );
        for (const line of action) {
          if (!line.clientSecret)
            throw new Error(
              "Your payment needs verification. Check purchase status shortly.",
            );
          const verification = await stripe.confirmCardPayment(
            line.clientSecret,
          );
          if (currentUser.current !== userId) return false;
          if (verification.error) {
            // Stripe may have changed the intent state even when its UI reports an error.
            result = await mutation.mutateAsync(original.request);
            if (currentUser.current !== userId) return false;
            await settle(result, original);
            if (currentUser.current !== userId) return false;
            setError(
              verification.error.message ?? "Your card could not be verified.",
            );
            return false;
          }
        }
        result = await mutation.mutateAsync(original.request);
      }
      return await settle(result, original);
    } catch (cause) {
      if (currentUser.current !== userId) return false;
      if (
        cause instanceof ApiError &&
        ["price_changed", "product_unavailable"].includes(cause.code)
      ) {
        if (persisted) {
          setError(
            "Your earlier purchase still needs reconciliation. Check its status or cancel the unpaid purchase.",
          );
          return false;
        }
        remember(null);
        setNeedsPriceReview(true);
        void qc.invalidateQueries({
          queryKey: ["supplements", userId, "catalog"],
        });
        setError(
          "The price or availability changed. Review the updated prices before confirming again.",
        );
      } else {
        if (
          !persisted &&
          cause instanceof ApiError &&
          [
            "supplements_unavailable",
            "checkout_unavailable",
            "product_unavailable",
            "price_unavailable",
            "price_changed",
            "no_payment_method",
            "invalid_payment_method",
            "no_shipping_address",
          ].includes(cause.code)
        )
          remember(null);
        setError(
          cause instanceof ApiError && cause.code === "price_unavailable"
            ? "We could not verify current prices. Please try again shortly."
            : cause instanceof Error
              ? cause.message
              : "We could not confirm the payment. Check purchase status before trying a new purchase.",
        );
      }
      return false;
    } finally {
      if (currentUser.current === userId) setBusy(false);
    }
  }

  async function cancel() {
    if (!attempt || currentUser.current !== userId) return;
    setBusy(true);
    setError(null);
    try {
      const result = await apiFetch<SupplementPurchaseResult>(
        "/supplements/purchase/cancel",
        {
          method: "POST",
          body: JSON.stringify({
            idempotencyKey: attempt.request.idempotencyKey,
          }),
        },
      );
      if (currentUser.current !== userId) return;
      await settle(result, attempt);
    } catch (cause) {
      if (currentUser.current !== userId) return;
      setError(
        cause instanceof Error
          ? cause.message
          : "We could not cancel the unpaid purchase. Check its status before retrying.",
      );
    } finally {
      if (currentUser.current === userId) setBusy(false);
    }
  }

  return {
    purchase,
    cancel,
    attempt,
    busy,
    error,
    needsPriceReview,
    reviewedPrices: () => {
      setNeedsPriceReview(false);
      setError(null);
    },
  };
}
